<script setup lang="ts">
import ContactAddress from "@/components/ContactAddress.vue"
import VarsityInlineConfirm from "@/components/VarsityInlineConfirm.vue"
import VarsityInlineConfirmButton from "@/components/VarsityInlineConfirmButton.vue"
import VarsityInlineConfirmGroup from "@/components/VarsityInlineConfirmGroup.vue"
import { createInlineConfirmCollection } from "@/composables/createInlineConfirm"
import type { ApproveAddressUpdateTask } from "@/model/app/approve-address-update-task"
import SimilarOrganization from "@/pages/Tasks/Tasks/SimilarOrganization.vue"
import { useHttpClient } from "@/vf"
import type { TasksProps } from "./tasks-props"

const props = defineProps<TasksProps<ApproveAddressUpdateTask>>()
const emit = defineEmits<{
    (e: "updated"): void
}>()
const http = useHttpClient()

const getInlineConfirmController = createInlineConfirmCollection(id => ({
    async action() {
        await http.post(`/account/${props.issue.owner!.id}/contact/task/${props.task.id}/approve-update`, {
            approved: id === "approve",
        })
    },
    afterAction() {
        emit("updated")
    },
}))
</script>

<template>
    <div class="p-4">
        <div class="row">
            <div class="col-md-8">
                <div
                    v-if="props.task.originalValues.addressLine !== props.task.newValues.addressLine"
                    class="alert alert-warning"
                >
                    {{ $t("@tasks:tasks.approve_address_update.organization_name_changed") }}
                </div>
                <div class="row">
                    <div class="col-md-4">
                        <dl>
                            <dt>{{ $t("@tasks:tasks.approve_address_update.original") }}</dt>
                            <dd>
                                <ContactAddress
                                    :address="{
                                        addressLine: props.task.originalValues.addressLine,
                                        addressLine2: props.task.originalValues.addressLine2,
                                        street: props.task.originalValues.street,
                                        streetNumber: props.task.originalValues.streetNumber,
                                        zip: props.task.originalValues.zip,
                                        city: props.task.originalValues.city,
                                        country: props.task.originalValues.country,
                                    }"
                                ></ContactAddress>
                            </dd>
                        </dl>
                    </div>
                    <div class="col-md-4">
                        <dl>
                            <dt>{{ $t("@tasks:tasks.approve_address_update.new") }}</dt>
                            <dd>
                                <ContactAddress
                                    :address="{
                                        addressLine: props.task.newValues.addressLine,
                                        addressLine2: props.task.newValues.addressLine2,
                                        street: props.task.newValues.street,
                                        streetNumber: props.task.newValues.streetNumber,
                                        zip: props.task.newValues.zip,
                                        city: props.task.newValues.city,
                                        country: props.task.newValues.country,
                                    }"
                                    :old-address="{
                                        addressLine: props.task.originalValues.addressLine,
                                        addressLine2: props.task.originalValues.addressLine2,
                                        street: props.task.originalValues.street,
                                        streetNumber: props.task.originalValues.streetNumber,
                                        zip: props.task.originalValues.zip,
                                        city: props.task.originalValues.city,
                                        country: props.task.originalValues.country,
                                    }"
                                ></ContactAddress>
                            </dd>
                        </dl>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-8">
                        <dl>
                            <SimilarOrganization
                                v-if="props.viewMode === 'admin'"
                                :organization-id="issue.owner?.id!"
                                :task-id="task.id!"
                            ></SimilarOrganization>
                        </dl>
                    </div>
                </div>
            </div>

            <div class="col-md-4">
                <div v-if="props.viewMode === 'admin' && props.task.state === 'open'">
                    <VarsityInlineConfirmGroup>
                        <VarsityInlineConfirmButton :controller="getInlineConfirmController('approve')">
                            {{ $t("@tasks:tasks.approve_address_update.approve.button") }}
                        </VarsityInlineConfirmButton>
                        <VarsityInlineConfirmButton
                            :controller="getInlineConfirmController('decline')"
                            class="btn btn-danger btn-shape-skewed"
                        >
                            {{ $t("@tasks:tasks.approve_address_update.decline.button") }}
                        </VarsityInlineConfirmButton>
                        <VarsityInlineConfirm :controller="getInlineConfirmController('approve')">
                            <template #confirmation>
                                {{ $t("@tasks:tasks.approve_address_update.approve.confirmation") }}
                            </template>
                            <template #success>
                                {{ $t("@tasks:tasks.approve_address_update.approve.success") }}
                            </template>
                        </VarsityInlineConfirm>
                        <VarsityInlineConfirm :controller="getInlineConfirmController('decline')">
                            <template #confirmation>
                                {{ $t("@tasks:tasks.approve_address_update.decline.confirmation") }}
                            </template>
                            <template #success>
                                {{ $t("@tasks:tasks.approve_address_update.decline.success") }}
                            </template>
                        </VarsityInlineConfirm>
                    </VarsityInlineConfirmGroup>
                </div>
            </div>
        </div>
    </div>
</template>
